import React, { useMemo, useState, useEffect } from 'react';
import styles from '../../styles/ReporteCalificacionesStyles/ReporteCalificaciones.module.css';

const PAGE_SIZE = 20;

function pctClass(p) {
  if (p >= 80) return styles.pctOk;
  if (p >= 50) return styles.pctMid;
  return styles.pctLow;
}

const ESTADO_LABELS = {
  COMPLETO: 'Completo',
  PARCIAL: 'Parcial',
  SIN_CAPTURA: 'Sin captura',
  SIN_ALUMNOS: 'Sin alumnos',
};

function estadoColor(estado) {
  switch (estado) {
    case 'COMPLETO': return { bg: '#e6f4ea', color: '#137333', border: '#34a853' };
    case 'PARCIAL': return { bg: '#fef7e0', color: '#9a6700', border: '#f9ab00' };
    case 'SIN_CAPTURA': return { bg: '#fdecea', color: '#c5221f', border: '#ea4335' };
    case 'SIN_ALUMNOS': return { bg: '#f1f3f4', color: '#5f6368', border: '#bdc1c6' };
    default: return { bg: '#f1f3f4', color: '#5f6368', border: '#bdc1c6' };
  }
}

function formatFecha(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('es-MX', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

/**
 * Tabla con paginación cliente.
 * Props: { resultados: Array, contexto: { ciclo, campus, seccion, parcial } }
 */
function TablaReporte({ resultados = [], contexto = {} }) {
  const [page, setPage] = useState(1);

  const total = resultados.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Reset a página 1 cuando cambian los datos
  useEffect(() => { setPage(1); }, [resultados]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return resultados.slice(start, start + PAGE_SIZE);
  }, [resultados, page]);

  // KPIs agregados
  const kpis = useMemo(() => {
    let totalEst = 0;
    let totalCalif = 0;
    let totalPend = 0;
    resultados.forEach(r => {
      totalEst += Number(r.total_estudiantes || 0);
      totalCalif += Number(r.total_calificados || 0);
      totalPend += Number(r.total_pendientes || 0);
    });
    const pctGlobal = totalEst > 0 ? (totalCalif / totalEst) * 100 : 0;
    return {
      totalGrupos: total,
      totalEst,
      totalCalif,
      totalPend,
      pctGlobal,
    };
  }, [resultados, total]);

  return (
    <>
      <div className={styles.reportHeader}>
        <h2 className={styles.reportTitle}>REPORTE DE CAPTURA DE CALIFICACIONES</h2>
      </div>

      <div className={styles.reportMeta}>
        <div className={styles.reportMetaItem}>
          <span className={styles.reportMetaLabel}>Ciclo:</span>
          <span>{contexto.ciclo || '—'}</span>
        </div>
        <div className={styles.reportMetaItem}>
          <span className={styles.reportMetaLabel}>Campus:</span>
          <span>{contexto.campus || 'Todos'}</span>
        </div>
        <div className={styles.reportMetaItem}>
          <span className={styles.reportMetaLabel}>Sección:</span>
          <span>{contexto.seccion || 'Todas'}</span>
        </div>
        <div className={styles.reportMetaItem}>
          <span className={styles.reportMetaLabel}>Parcial:</span>
          <span>{contexto.parcial || '—'}</span>
        </div>
        <div className={styles.reportMetaItem}>
          <span className={styles.reportMetaLabel}>Fuente:</span>
          <span>Rúbrica</span>
        </div>
      </div>

      <div className={styles.summaryStrip}>
        <div className={styles.summaryItem}>
          <span className={styles.summaryItemLabel}>Total de grupos</span>
          <span className={styles.summaryItemValue}>{kpis.totalGrupos}</span>
        </div>
        <div className={styles.summaryItem}>
          <span className={styles.summaryItemLabel}>Estudiantes</span>
          <span className={styles.summaryItemValue}>{kpis.totalEst}</span>
        </div>
        <div className={styles.summaryItem}>
          <span className={styles.summaryItemLabel}>Evaluados</span>
          <span className={styles.summaryItemValue}>{kpis.totalCalif}</span>
        </div>
        <div className={styles.summaryItem}>
          <span className={styles.summaryItemLabel}>Pendientes</span>
          <span className={styles.summaryItemValue}>{kpis.totalPend}</span>
        </div>
        <div className={styles.summaryItem}>
          <span className={styles.summaryItemLabel}>% global</span>
          <span className={`${styles.summaryItemValue} ${pctClass(kpis.pctGlobal)}`}>
            {kpis.pctGlobal.toFixed(2)}%
          </span>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.reportTable}>
          <thead>
            <tr>
              <th>Asesor</th>
              <th>Campus</th>
              <th>Sección</th>
              <th>Programa</th>
              <th>Clave de Grupo</th>
              <th>Materia</th>
              <th className={styles.cellCenter}>Total</th>
              <th className={styles.cellCenter}>Evaluados</th>
              <th className={styles.cellCenter}>Pendientes</th>
              <th className={styles.cellCenter}>%</th>
              <th className={styles.cellCenter}>Estado</th>
              <th className={styles.cellCenter}>Última actualización</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={12} className={styles.cellCenter} style={{ padding: '20px', color: '#999' }}>
                  No hay grupos para mostrar.
                </td>
              </tr>
            ) : (
              pageRows.map((r, idx) => {
                const ec = estadoColor(r.estado_captura);
                return (
                  <tr key={`${r.id_grupo}-${idx}`}>
                    <td>{r.asesor}</td>
                    <td>{r.campus || '—'}</td>
                    <td>{r.seccion || '—'}</td>
                    <td>{r.programa || '—'}</td>
                    <td>{r.clave_grupo}</td>
                    <td>{r.materia}</td>
                    <td className={styles.cellCenter}>{r.total_estudiantes}</td>
                    <td className={styles.cellCenter}>{r.total_calificados}</td>
                    <td className={styles.cellCenter}>{r.total_pendientes ?? Math.max(0, (r.total_estudiantes || 0) - (r.total_calificados || 0))}</td>
                    <td className={`${styles.cellCenter} ${pctClass(Number(r.porcentaje || 0))}`}>
                      {Number(r.porcentaje || 0).toFixed(2)}%
                    </td>
                    <td className={styles.cellCenter}>
                      <span style={{
                        display: 'inline-block',
                        padding: '3px 10px',
                        borderRadius: 12,
                        fontSize: 12,
                        fontWeight: 600,
                        background: ec.bg,
                        color: ec.color,
                        border: `1px solid ${ec.border}`,
                      }}>
                        {ESTADO_LABELS[r.estado_captura] || r.estado_captura || '—'}
                      </span>
                    </td>
                    <td className={styles.cellCenter}>{formatFecha(r.fecha_actualizacion)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {total > PAGE_SIZE && (
        <div className={styles.pagination}>
          <span className={styles.pageInfo}>
            Mostrando {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)} de {total}
          </span>
          <div className={styles.pageButtons}>
            <button
              type="button"
              className={styles.pageButton}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              « Anterior
            </button>
            <span className={styles.pageInfo}>Página {page} de {totalPages}</span>
            <button
              type="button"
              className={styles.pageButton}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Siguiente »
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default TablaReporte;
